import { Box, Button, Col, Row, Text } from '@opendoor/bricks-next';
import type { Awaited, EntryComponent, LoaderFn } from 'cms/entries/entries';
import { PressBox } from 'components/landing-pages-v2/shared/hero/PressBox';
import type { ILpComponentHeroCta, ILpComponentHeroCtaFields } from 'declarations/contentful';
import { motion } from 'framer-motion';

import {
  generatePresetPictureSet,
  generatePresetPictureSetProps,
} from '../shared/performance/pictures';
import type {
  PictureSourceSpec,
  PresetPictureSet,
  PresetPictureSetOptions,
} from '../shared/performance/pictures';
import ContextualField from './ContextualField';
import type { ICtaStyles } from './shared/Buttons';
import Container from './shared/Container';
import ExtraCtas from './shared/ExtraCtas';
import { OpendoorLogo } from './shared/OpendoorLogo';
import { getComponentThemeColors } from './shared/styles/ComponentTheme';
import { markdownToReactElement } from './shared/utils';

const HERO_IMAGE_SIZES: PresetPictureSet = [
  {
    minWidth: 0,
    maxWidth: 375,
    preset: 'square-256',
  },
  {
    minWidth: 375,
    maxWidth: 768,
    preset: 'square-512',
  },
  {
    minWidth: 768,
    maxWidth: 1201,
    preset: 'square-512',
  },
  {
    minWidth: 1201,
    maxWidth: null,
    preset: 'square-1024',
  },
];

export const HERO_IMAGE_PROPS: Omit<PresetPictureSetOptions, 'photoUrl'> = {
  sizes: HERO_IMAGE_SIZES,
  debugImageSizes: false,
  lazy: false,
  preload: true,
  borderRadius: '$8x',
};

const showLogo = {
  start: {
    opacity: 0,
  },
  end: {
    opacity: 1,
    transition: {
      ease: 'linear',
      delay: 0,
      duration: 0.3,
    },
  },
};

const showArch = {
  start: {
    y: 0,
  },
  end: {
    y: '-100%',
    transition: {
      ease: [0.55, 0, 0.15, 1],
      delay: 0.35,
      duration: 1,
    },
  },
};

const showText = {
  start: {
    opacity: 0,
  },
  end: {
    opacity: 1,
    transition: {
      delay: 1.4,
      duration: 0.3,
      type: 'linear',
    },
  },
};

// Set default theme to generate button colors
const defaultTheme = getComponentThemeColors('White 0');

const ctaStyleToButtonVariant = (ctaStyle?: ICtaStyles): 'primary' | 'secondary' => {
  switch (ctaStyle) {
    case 'Secondary':
    case 'Tertiary':
      return 'secondary';
    default:
      return 'primary';
  }
};

const DoorwayLayout = ({ entry }: { entry: ILpComponentHeroCta }) => {
  const { fields } = entry;
  const hasLogos = fields.pressLogos && fields.pressLogos?.length > 1;

  const showPressSection = fields.showPressSection;

  return (
    <Row className="lp-section" display="block" mx="auto" maxWidth="$1440x">
      <Box
        position="relative"
        width="100%"
        height="$616x"
        $largerThanMD={{ height: '$768x' }}
        gap="$8x"
        $platform-web={{
          display: 'grid',
          gridTemplate: '1fr / 1fr',
        }}
      >
        <Box
          zIndex={5}
          $platform-web={{
            display: 'grid',
            gridArea: '1 / 1 / -1 / -1',
            placeItems: 'end center',
          }}
        >
          <motion.div variants={showText} initial="start" animate="end">
            <Col
              gap="$16x"
              pb={hasLogos ? '$8x' : '$16x'}
              $largerThanSM={hasLogos ? undefined : { pb: '$40x' }}
              px="$10x"
              $platform-web={{ textAlign: 'center' }}
            >
              <Col gap="$8x">
                <Text
                  tag="h1"
                  color="$backgroundPrimary"
                  typography={fields.titleSize === 'Small' ? '$headerSmall' : '$headerMedium'}
                  $largerThanSM={{
                    typography: fields.titleSize === 'Small' ? '$headerMedium' : '$displaySmall',
                  }}
                  $largerThanMD={{
                    typography:
                      // eslint-disable-next-line no-nested-ternary -- Grandfathered violation
                      fields.titleSize === 'XLarge'
                        ? '$displayLarge'
                        : fields.titleSize === 'Small'
                          ? '$headerMedium'
                          : '$displaySmall',
                  }}
                  mx="auto"
                  maxWidth="$516x"
                >
                  {markdownToReactElement(fields.title || '')}
                </Text>
                {fields.desc && (
                  <Text
                    tag="p"
                    color="$backgroundPrimary"
                    typography="$bodyMedium"
                    $largerThanMD={{
                      typography: '$bodyLarge',
                      mx: 'auto',
                    }}
                    mx="$10x"
                    $smallerThanMD={{
                      maxWidth: '$382x',
                    }}
                  >
                    {markdownToReactElement(fields.desc || '')}
                  </Text>
                )}
              </Col>
              {((fields.ctaDisplayText && fields.ctaUrl) || fields.extraCtas) && (
                <Row
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="center"
                  gap="$8x"
                  pb={hasLogos ? '$16x' : undefined}
                >
                  {fields.ctaDisplayText && fields.ctaUrl && (
                    <Button
                      aria-label={`Go to page ${fields.ctaDisplayText}`}
                      analyticsName={`cosmos-landing-page-cta-standalone-${fields?.analyticsId}`}
                      variant={ctaStyleToButtonVariant(fields.ctaStyle)}
                      size="medium"
                      label={fields.ctaDisplayText}
                      href={fields.ctaUrl}
                    />
                  )}
                  {fields.extraCtas && (
                    <ExtraCtas ctas={fields.extraCtas} componentTheme={defaultTheme} />
                  )}
                </Row>
              )}
              {fields.contextualField && (
                <Row justifyContent="center">
                  <ContextualField
                    theme={defaultTheme}
                    contextFields={fields.contextualField}
                    inputLocation="hero-cta"
                  />
                </Row>
              )}
              {fields.bottomCtaDisplayText && fields.bottomCtaUrl && (
                <Button
                  aria-label={`Go to page ${fields?.bottomCtaDisplayText}`}
                  analyticsName={`cosmos-landing-page-cta-standalone-${fields?.analyticsId}`}
                  variant="secondaryInverse"
                  size="medium"
                  label={fields.bottomCtaDisplayText}
                  href={fields.bottomCtaUrl}
                />
              )}
            </Col>
          </motion.div>
        </Box>
        <motion.div
          variants={showArch}
          initial="start"
          animate="end"
          style={{
            gridArea: '1 / 1 / -1 / -1',
            height: '100%',
            width: '100%',
            pointerEvents: 'none',
            position: 'absolute',
            top: 0,
            zIndex: 2,
          }}
        >
          <Box
            backgroundColor="$backgroundPrimary"
            minHeight="100%"
            width="100%"
            $platform-web={{
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <motion.div variants={showLogo} initial="start" animate="end">
              <OpendoorLogo />
            </motion.div>
            <Row
              height="$10x"
              width="100%"
              backgroundColor="$backgroundPrimary"
              left={0}
              position="absolute"
              top="100%"
              transform="translateY(-8px)"
            />
            <svg
              style={{
                height: 'auto',
                left: 0,
                position: 'absolute',
                top: '100%',
                width: '100%',
              }}
              xmlns="http://www.w3.org/2000/svg"
              width="1442"
              height="737"
              viewBox="0 0 1442 737"
              fill="none"
            >
              <path
                d="M1442 0H0V737C0 338.803 322.803 16 721 16C1119.2 16 1442 338.803 1442 737V0Z"
                fill="#FFF"
              />
            </svg>
          </Box>
        </motion.div>
        <Box
          alignSelf="flex-end"
          backgroundColor="$backgroundInversePrimary"
          height="100%"
          zIndex={1}
          overflow="hidden"
          $platform-web={{ gridArea: '1 / 1 / -1 / -1' }}
        >
          {fields.doorwayImageUrl && (
            <Row height="100%" width="100%">
              {generatePresetPictureSet({
                ...HERO_IMAGE_PROPS,
                borderRadius: undefined,
                photoUrl: fields?.doorwayImageUrl,
              })}
            </Row>
          )}
        </Box>
      </Box>
      {!!showPressSection && (
        <motion.div variants={showText} initial="start" animate="end">
          <PressBox width="100%" />
        </motion.div>
      )}
    </Row>
  );
};

const CollageLayout = ({ entry }: { entry: ILpComponentHeroCta }) => {
  const { fields } = entry;
  const minH = 'calc(90vh - 56px)';

  const collageImageTopLeft = fields.collageImageUrlTopLeft;
  const collageImageBottomLeft = fields.collageImageUrlBottomLeft;
  const collageImageBottomRight = fields.collageImageUrlBottomRight;

  const showPressSection = fields.showPressSection;

  return (
    <Container>
      <Box
        flexDirection="column"
        gap="$8x"
        $largerThanMD={{
          minHeight: minH,
          flexDirection: 'row',
        }}
      >
        <Col
          gap="$8x"
          $largerThanMD={{ alignSelf: 'center', flex: 5, minWidth: 0 }}
          $largerThanLG={{ flex: 4 }}
        >
          <Text
            tag="h1"
            typography={fields.titleSize === 'Small' ? '$headerSmall' : '$headerMedium'}
            $largerThanSM={{
              typography: fields.titleSize === 'Small' ? '$headerMedium' : '$displaySmall',
              maxWidth: '$636x',
            }}
          >
            {markdownToReactElement(fields.title || '')}
          </Text>
          {fields.desc && (
            <Text
              tag="p"
              typography={fields.titleSize === 'Small' ? '$bodyLarge' : '$bodyXlarge'}
              color="$contentPrimary"
              $largerThanMD={{
                typography: '$bodyLarge',
                maxWidth: '$450x',
              }}
            >
              {markdownToReactElement(fields.desc || '')}
            </Text>
          )}
          {((fields.ctaDisplayText && fields.ctaUrl) || fields.extraCtas) && (
            <Row flexWrap="wrap" alignItems="center" gap="$8x" pb="$6x">
              {fields.ctaDisplayText && fields.ctaUrl && (
                <Button
                  aria-label={`Go to page ${fields.ctaDisplayText}`}
                  analyticsName={`cosmos-landing-page-cta-standalone-${fields?.analyticsId}`}
                  variant={ctaStyleToButtonVariant(fields.ctaStyle)}
                  size="medium"
                  label={fields.ctaDisplayText}
                  href={fields.ctaUrl}
                />
              )}
              {fields.extraCtas && (
                <ExtraCtas ctas={fields.extraCtas} componentTheme={defaultTheme} />
              )}
            </Row>
          )}
          {fields.contextualField && (
            <ContextualField
              theme={defaultTheme}
              contextFields={fields.contextualField}
              inputLocation="hero-cta"
            />
          )}
        </Col>
        <Col
          justifyContent="center"
          $largerThanMD={{ flex: 7 }}
          $largerThanLG={{ flex: 8 }}
          gap="$4x"
          $largerThanSM={{ gap: '$8x' }}
        >
          <Row gap="$4x" $largerThanSM={{ gap: '$8x' }}>
            <Col
              height="$180x"
              $largerThanMD={{ height: '$250x' }}
              overflow="hidden"
              flexGrow={1}
              flexShrink={1}
              flexBasis={0}
            >
              {collageImageTopLeft && (
                <Box height="100%">
                  {generatePresetPictureSet({
                    ...HERO_IMAGE_PROPS,
                    photoUrl: collageImageTopLeft,
                  })}
                </Box>
              )}
            </Col>
            <Row
              backgroundColor="$backgroundTertiary"
              height="$180x"
              $largerThanMD={{ height: '$250x' }}
              width="$110x"
              $largerThanSM={{ width: '$200x' }}
              flexShrink={0}
              pb="$4x"
              alignItems="center"
              justifyContent="center"
              overflow="hidden"
              // style prop used because bricks-next doesn't support custom border-radius
              // values (only tokens), and $platform-web gets overridden by the component's
              // inline styles. style prop applies last and ensures the arch shape renders.
              style={{ borderRadius: '99em 99em 16em 16em' }}
            >
              <Row width="$34x" $largerThanSM={{ width: '$48x' }} $largerThanMD={{ width: '$64x' }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="100%"
                  height="auto"
                  viewBox="0 0 34 49"
                  fill="none"
                >
                  <path
                    d="M24.4441 18.9269C24.4441 7.0532 22.4319 2.13138 16.8995 2.13138C12.0206 2.13138 9.55613 5.58163 9.55613 17.557C9.55613 29.3799 11.4675 34.2511 17.1006 34.2511C22.08 34.2511 24.4441 30.8513 24.4441 18.9269ZM0 18.6226C0 5.2772 9.10359 0 17.2013 0C25.8522 0 34 5.22662 34 17.8107C34 31.2573 24.8966 36.3825 16.7987 36.3825C8.14802 36.3825 0 30.8009 0 18.6226ZM32.2246 47.1186C31.3991 46.014 28.2194 41.7583 28.2194 41.7583C27.9211 41.359 27.4099 41.118 26.8612 41.118H7.13884C6.59022 41.118 6.07901 41.359 5.78067 41.7583L1.77607 47.1179C1.64903 47.2878 1.58162 47.4857 1.58162 47.688V48.5061C1.58162 48.7789 1.80076 49 2.07118 49H31.9289C32.1993 49 32.4185 48.7789 32.4185 48.5061V47.688C32.4185 47.4857 32.3516 47.2884 32.2246 47.1186Z"
                    fill="#9D9893"
                  />
                </svg>
              </Row>
            </Row>
          </Row>
          <Row gap="$4x" $largerThanSM={{ gap: '$8x' }} width="100%">
            <Row
              height="$180x"
              $largerThanMD={{ height: '$250x' }}
              overflow="hidden"
              flexGrow={1.7}
              flexShrink={1}
              flexBasis={0}
            >
              {collageImageBottomLeft && (
                <Box width="100%" height="100%">
                  {generatePresetPictureSet({
                    ...HERO_IMAGE_PROPS,
                    photoUrl: collageImageBottomLeft,
                  })}
                </Box>
              )}
            </Row>
            <Row
              height="$180x"
              $largerThanMD={{ height: '$250x' }}
              overflow="hidden"
              flexGrow={1.3}
              flexShrink={1}
              flexBasis={0}
            >
              {collageImageBottomRight && (
                <Box width="100%" height="100%">
                  {generatePresetPictureSet({
                    ...HERO_IMAGE_PROPS,
                    photoUrl: collageImageBottomRight,
                  })}
                </Box>
              )}
            </Row>
          </Row>
        </Col>
      </Box>
      {!!showPressSection && (
        <Row pt="$16x">
          <PressBox
            // @ts-expect-error we don't have 20px in novo.radii
            borderRadius={['20px', null]}
          />
        </Row>
      )}
    </Container>
  );
};

const RenderHeroCTA = (entry: ILpComponentHeroCta) => {
  const { fields } = entry;

  return fields.layout === 'Doorway' ? (
    <DoorwayLayout entry={entry} />
  ) : (
    <CollageLayout entry={entry} />
  );
};

type IHeroCTALoaderReturn = {
  preloadImages: PictureSourceSpec;
};

const getHeroCTAImageUrls = (fields: ILpComponentHeroCtaFields) => {
  if (fields.layout === 'Doorway') {
    return fields.doorwayImageUrl ? [fields.doorwayImageUrl] : [];
  }

  return [
    fields.collageImageUrlTopLeft,
    fields.collageImageUrlBottomLeft,
    fields.collageImageUrlBottomRight,
  ].filter((url): url is string => Boolean(url));
};

const heroCTALoader: LoaderFn<ILpComponentHeroCta, IHeroCTALoaderReturn> = async (input) => {
  const preloadImages = getHeroCTAImageUrls(input.fields).flatMap((photoUrl) => {
    const { sources } = generatePresetPictureSetProps({
      ...HERO_IMAGE_PROPS,
      photoUrl,
    });
    return sources;
  });

  const loaderReturn: IHeroCTALoaderReturn = {
    preloadImages,
  };

  return loaderReturn;
};

const HeroCTA: EntryComponent<ILpComponentHeroCta, Awaited<ReturnType<typeof heroCTALoader>>> = {
  render: RenderHeroCTA,
  loader: heroCTALoader,
};

export default HeroCTA;
